import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/cn';
import type { QueueItem } from '@/features/queue/types';
import { DashCard, ViewAllButton, drName, fmtMin } from './DashCard';

// Same threshold as QueueView.tsx's UNASSIGNED_FLAG_MINUTES: past this, a
// patient with nobody picked is called out.
const UNASSIGNED_FLAG_MINUTES = 20;

const STATUS_VARIANT: Record<QueueItem['status'], 'warn' | 'info' | 'success'> = {
  Waiting: 'warn', 'On Hold': 'warn', 'In Consultation': 'info', Completed: 'success',
};
const PRIORITY_VARIANT: Record<QueueItem['priority'], 'success' | 'caution' | 'danger'> = {
  Normal: 'success', Urgent: 'caution', Emergency: 'danger',
};
const COLUMNS = ['Token', 'Visit ID', 'Patient', 'Priority', 'Vitals', 'Status', 'Wait time', 'Time'];

/**
 * Today's Queue — docs/design/dashboard region 5: eight columns, newest
 * first. The body scrolls inside the card past a few rows so a busy morning
 * cannot push Quick Actions below the fold; "View all" opens the full queue.
 */
export function QueueCard({ queue, onOpenVisit, onViewAll }: {
  queue: QueueItem[];
  onOpenVisit?: (visitCode: string) => void;
  onViewAll: () => void;
}) {
  const rows = [...queue].reverse();
  const now = Date.now();
  return (
    <DashCard title="Today's Queue" action={<ViewAllButton onClick={onViewAll} />}>
      {rows.length === 0 ? (
        <div className="flex flex-1 items-center justify-center py-8 text-[13px] text-ink-muted">No patients in queue yet</div>
      ) : (
        <div className="mt-4 max-h-[200px] overflow-auto">
          <table className="w-full min-w-[440px] border-collapse text-[11.5px]">
            <thead className="sticky top-0 bg-card">
              <tr>
                {COLUMNS.map(h => (
                  <th key={h} className="border-b border-line bg-card px-1 pb-3 pt-0 text-left text-[11.5px] font-medium text-ink">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(item => {
                const waiting = item.status === 'Waiting' || item.status === 'On Hold';
                const waitMin = waiting ? Math.max(0, Math.round((now - new Date(item.added_at).getTime()) / 60000)) : null;
                const unassigned = item.unassigned_elapsed_minutes;
                const flag = unassigned != null && unassigned >= UNASSIGNED_FLAG_MINUTES;
                const open = () => { if (item.visit_code) onOpenVisit?.(item.visit_code); };
                return (
                  <tr key={item.id} onClick={open} className={cn('align-top', item.visit_code && 'cursor-pointer hover:bg-muted/40')}>
                    <td className="border-b border-line px-1 py-3 font-semibold text-teal">{item.display_token ? `#${item.display_token}` : '—'}</td>
                    <td className="border-b border-line px-1 py-3">
                      {item.visit_code
                        ? <button type="button" onClick={e => { e.stopPropagation(); open(); }} className="whitespace-nowrap font-mono text-[10px] text-teal hover:underline">{item.visit_code}</button>
                        : '—'}
                    </td>
                    <td className="border-b border-line px-1 py-3">
                      <div className="font-semibold">{item.name}</div>
                      <div className="text-[11px] text-ink-muted">{item.gender}{item.age != null ? `, ${item.age}y` : ''}</div>
                    </td>
                    <td className="border-b border-line px-1 py-3"><Badge variant={PRIORITY_VARIANT[item.priority] ?? 'success'} className="rounded px-1.5 text-[10px] font-medium">{item.priority || 'Normal'}</Badge></td>
                    <td className="whitespace-nowrap border-b border-line px-1 py-3 font-mono text-[10.5px]" title="Blood pressure (latest)">
                      {item.bp_systolic ? `${item.bp_systolic}/${item.bp_diastolic ?? '—'}` : '—'}
                    </td>
                    <td className="border-b border-line px-1 py-3"><Badge variant={STATUS_VARIANT[item.status] ?? 'warn'} className="whitespace-normal rounded px-1.5 text-[10px] font-medium leading-tight">{item.status}</Badge></td>
                    <td className="border-b border-line px-1 py-3">
                      <div className="font-medium">{waitMin != null ? fmtMin(waitMin) : '—'}</div>
                      <div className={cn('text-[10px]', flag ? 'font-bold text-danger-ink' : 'text-ink-muted')}>
                        {item.doctor_name ? drName(item.doctor_name) : waiting ? `unassigned${flag ? ' ⚠' : ''}` : ''}
                      </div>
                    </td>
                    <td className="whitespace-nowrap border-b border-line px-1 py-3 text-[10.5px] tabular-nums">
                      {new Date(item.added_at).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </DashCard>
  );
}
